import React from 'react';
import { Bitcoin, Coins, Gem, Cpu, TrendingUp, Clock, Zap } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';
import type { ExchangeRates } from '../../utils/currencyUtils';
import { fallbackData } from '../../utils/fallbackData';

interface CoinSelectorProps {
  selectedCoin: string | null;
  onCoinSelect: (coin: string) => void;
  marketData: Record<string, any>;
  isLoading: boolean;
  currency: string;
  exchangeRates: ExchangeRates;
}

export function CoinSelector({ 
  selectedCoin, 
  onCoinSelect, 
  marketData = fallbackData, // Use fallback data as default
  isLoading, 
  currency,
  exchangeRates 
}: CoinSelectorProps) {
  // Get network stats dynamically with fallback values
  const getNetworkStats = (coin: string) => {
    const defaultStats = {
      BTC: { hashrate: '650.0 EH/s', blockTime: '10 min', algorithm: 'SHA-256' },
      LTC: { hashrate: '950.0 TH/s', blockTime: '2.5 min', algorithm: 'Scrypt' },
      ETH: { hashrate: '1.2 PH/s', blockTime: '12 sec', algorithm: 'Ethash' },
      KDA: { hashrate: '750.0 TH/s', blockTime: '30 sec', algorithm: 'Blake2s' }
    };

    const data = marketData[coin] || fallbackData[coin];
    if (!data) {
      console.warn(`No data for ${coin}, using default stats`);
      return defaultStats[coin as keyof typeof defaultStats];
    }

    const formatHashrate = (hashrate: number) => {
      if (hashrate >= 1e6) {
        return `${(hashrate / 1e6).toFixed(1)} EH/s`;
      } else if (hashrate >= 1e3) {
        return `${(hashrate / 1e3).toFixed(1)} PH/s`;
      }
      return `${hashrate.toFixed(1)} TH/s`;
    };

    const formatBlockTime = (seconds: number) => {
      if (seconds < 60) {
        return `${seconds} sec`;
      }
      return `${(seconds / 60).toFixed(1)} min`;
    };

    return {
      hashrate: formatHashrate(data.networkHashrate),
      blockTime: formatBlockTime(data.blockTime),
      algorithm: data.algorithm
    };
  };

  const COINS = [
    {
      id: 'BTC',
      name: 'Bitcoin',
      icon: Bitcoin,
      color: 'orange',
      description: 'SHA-256',
      stats: getNetworkStats('BTC')
    },
    {
      id: 'LTC',
      name: 'Litecoin',
      icon: Coins,
      color: 'blue',
      description: 'Scrypt',
      stats: getNetworkStats('LTC')
    },
    {
      id: 'ETH',
      name: 'Ethereum',
      icon: Gem,
      color: 'purple',
      description: 'Ethash',
      stats: getNetworkStats('ETH')
    },
    {
      id: 'KDA',
      name: 'Kadena',
      icon: Cpu,
      color: 'green',
      description: 'Blake2s',
      stats: getNetworkStats('KDA')
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {COINS.map(({ id, name, icon: Icon, color, description, stats }) => {
        const data = marketData[id] || fallbackData[id];
        const price = data?.price || 0;
        const priceChange = data?.change24h || 0;
        
        return (
          <button
            key={id}
            onClick={() => onCoinSelect(id)}
            className={`group relative rounded-3xl transition-all duration-300 ${
              selectedCoin === id
                ? 'ring-2 ring-blue-500 shadow-xl scale-[1.02]'
                : 'hover:scale-[1.01] hover:shadow-lg'
            }`}
          >
            {/* Interactive Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden">
              <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                <div className="absolute top-0 left-0 w-full h-full">
                  <svg
                    className="w-full h-full transform group-hover:scale-110 transition-transform duration-700"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <pattern
                        id={`grid-${id}`}
                        width="10"
                        height="10"
                        patternUnits="userSpaceOnUse"
                      >
                        <path
                          d="M 10 0 L 0 0 0 10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="0.5"
                        />
                      </pattern>
                    </defs>
                    <rect width="100" height="100" fill={`url(#grid-${id})`} />
                  </svg>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="relative p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-3 bg-${color}-50 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-8 h-8 text-${color}-500`} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 text-lg">{name}</h3>
                    <p className="text-sm text-gray-500">{stats.algorithm}</p>
                  </div>
                </div>
              </div>

              <div className={`space-y-4 ${isLoading ? 'animate-pulse' : ''}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Price
                  </span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(price, currency, exchangeRates)}
                    </div>
                    <div className={`text-xs ${priceChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Network Hashrate
                  </span>
                  <span className="font-semibold text-gray-900">{stats.hashrate}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Block Time
                  </span>
                  <span className="font-semibold text-gray-900">{stats.blockTime}</span>
                </div>
              </div>

              {/* Selection Indicator */}
              {selectedCoin === id && (
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full border border-blue-100">
                    Selected
                  </span>
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}